import React from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import useSchool from '../hooks/useSchool';
import { StudentList, ClassList, TeacherList } from '../components';
import { getSchoolLevelLabel } from '../constants';

const SchoolDetailPage: React.FC = () => {
  const { schoolId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get active tab from URL params, default to 'classes'
  const activeTab = (searchParams.get('tab') as 'students' | 'classes' | 'teachers') || 'classes';
  
  // Function to update active tab
  const setActiveTab = (tab: 'students' | 'classes' | 'teachers') => {
    setSearchParams({ tab });
  };

  // Use school detail hook
  const { useSchoolDetail } = useSchool();
  const { 
    data: school, 
    isLoading: isLoadingSchool, 
    isError: isErrorSchool,
    error: schoolError 
  } = useSchoolDetail(schoolId || '');

  const getTypeText = (type: string) => {
    switch (type) {
      case 'public':
        return 'Trường Công lập';
      case 'private':
        return 'Trường Tư thục';
      case 'international':
        return 'Trường Quốc tế';
      case 'vocational':
        return 'Trường Nghề';
      default:
        return type;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Hoạt động';
      case 'inactive':
        return 'Không hoạt động';
      case 'pending':
        return 'Chờ duyệt';
      default:
        return status || 'Chưa có trạng thái';
    }
  };

  const formatAddress = (address: string | any) => {
    if (typeof address === 'string') {
      return address;
    }
    
    if (typeof address === 'object' && address !== null) {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.district) parts.push(address.district);
      if (address.city) parts.push(address.city);
      if (address.province) parts.push(address.province);
      if (address.country) parts.push(address.country);
      
      return parts.length > 0 ? parts.join(', ') : 'Chưa cập nhật';
    }
    
    return 'Chưa cập nhật';
  };

  // Loading state
  if (isLoadingSchool) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-4 mb-2">
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="flex items-center space-x-4 mt-2">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
            <div className="h-4 w-80 bg-gray-200 rounded animate-pulse mt-1"></div>
          </div>
          <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        {/* School Info Card Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-1"></div>
                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filters Skeleton */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
            </div>
            <div className="flex gap-3">
              <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
              <div className="h-10 w-36 bg-gray-200 rounded-md animate-pulse"></div>
              <div className="h-10 w-36 bg-gray-200 rounded-md animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Summary Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="ml-4">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-6 w-16 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

                 {/* Dynamic List Section Skeleton */}
         <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
           <div className="px-6 py-4 border-b border-gray-200">
             <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
           </div>
           <div className="p-6">
             <div className="space-y-4">
               {/* List Item Skeletons */}
               {[1, 2, 3, 4, 5].map((i) => (
                 <div key={i} className="flex items-center space-x-4 p-4 border border-gray-100 rounded-lg">
                   <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                   <div className="flex-1 space-y-2">
                     <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                     <div className="h-3 w-48 bg-gray-200 rounded animate-pulse"></div>
                     <div className="h-3 w-24 bg-gray-200 rounded animate-pulse"></div>
                   </div>
                   <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                 </div>
               ))}
             </div>
           </div>
         </div>
      </div>
    );
  }

  // Error state
  if (isErrorSchool || !school) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Lỗi tải dữ liệu</h2>
          <p className="text-gray-600 mb-4">
            {schoolError?.message || 'Không tìm thấy thông tin trường học'}
          </p>
          <Link 
            to="/admin/school"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Quay lại danh sách
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-4 mb-2">
            <Link 
              to="/admin/school"
              className="text-blue-600 hover:text-blue-800"
            >
              ← Quay lại danh sách
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{school.name}</h1>
          <div className="flex items-center space-x-4 mt-2">
            <span className="text-sm text-gray-600">{getTypeText(school.type)} - {getSchoolLevelLabel(school.level)}</span>
            <span className="text-sm text-gray-600">•</span>
            <span className="text-sm text-gray-600">Mã: {school.code}</span>
            <span className="text-sm text-gray-600">•</span>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              school.status === 'active' ? 'bg-green-100 text-green-800' : 
              school.status === 'inactive' ? 'bg-red-100 text-red-800' :
              school.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {getStatusText(school.status)}
            </span>
          </div>
        </div>
        
      </div>

      {/* School Info Card */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin cơ bản</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-600">Mã trường</label>
            <p className="text-sm text-gray-900 mt-1">{school.code}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Email</label>
            <p className="text-sm text-gray-900 mt-1">{school.email || 'Chưa cập nhật'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Điện thoại</label>
            <p className="text-sm text-gray-900 mt-1">{school.phone || 'Chưa cập nhật'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Website</label>
            <p className="text-sm text-gray-900 mt-1">
              {school.website ? (
                <a 
                  href={school.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  {school.website}
                </a>
              ) : (
                'Chưa cập nhật'
              )}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Hiệu trưởng</label>
            <p className="text-sm text-gray-900 mt-1">{school.principal || 'Chưa cập nhật'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Năm thành lập</label>
            <p className="text-sm text-gray-900 mt-1">{school.foundedYear || 'Chưa cập nhật'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Địa chỉ</label>
            <p className="text-sm text-gray-900 mt-1">{formatAddress(school.address)}</p>
          </div>
        </div>
        {school.metadata?.description && (
          <div className="mt-6">
            <label className="text-sm font-medium text-gray-600">Mô tả</label>
            <p className="text-sm text-gray-900 mt-1">{school.metadata.description}</p>
          </div>
        )}
      </div>



             {/* Summary Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
         <div 
           className={`p-6 rounded-lg shadow-sm border cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group ${
             activeTab === 'students' 
               ? 'bg-green-50 border-green-200 shadow-md' 
               : 'bg-white border-gray-200'
           }`}
           onClick={() => setActiveTab('students')}
           title="Xem danh sách học sinh"
         >
           <div className="flex items-center">
             <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
               <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
               </svg>
             </div>
             <div className="ml-4">
               <p className="text-sm font-medium text-gray-600">Học sinh</p>
               <p className="text-2xl font-semibold text-gray-900">{school.studentCount?.toLocaleString() || 0}</p>
             </div>
           </div>
         </div>

         <div 
           className={`p-6 rounded-lg shadow-sm border cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group ${
             activeTab === 'classes' 
               ? 'bg-orange-50 border-orange-200 shadow-md' 
               : 'bg-white border-gray-200'
           }`}
           onClick={() => setActiveTab('classes')}
           title="Xem danh sách lớp học"
         >
           <div className="flex items-center">
             <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
               <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
               </svg>
             </div>
             <div className="ml-4">
               <p className="text-sm font-medium text-gray-600">Lớp</p>
               <p className="text-2xl font-semibold text-gray-900">{school.classCount || 0}</p>
             </div>
           </div>
         </div>

         <div 
           className={`p-6 rounded-lg shadow-sm border cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 group ${
             activeTab === 'teachers' 
               ? 'bg-blue-50 border-blue-200 shadow-md' 
               : 'bg-white border-gray-200'
           }`}
           onClick={() => setActiveTab('teachers')}
           title="Xem danh sách giảng viên"
         >
           <div className="flex items-center">
             <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
               <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
               </svg>
             </div>
             <div className="ml-4">
               <p className="text-sm font-medium text-gray-600">Giáo viên</p>
               <p className="text-2xl font-semibold text-gray-900">{school.teacherCount || 0}</p>
             </div>
           </div>
         </div>
      </div>

             {/* Dynamic List Section */}
       <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
         {/* Content */}
         <div className="overflow-hidden">
                                   {activeTab === 'students' && (
              <StudentList
                schoolId={schoolId || ''}
                schoolName={school?.name || ''}
              />
            )}
            {activeTab === 'classes' && (
              <ClassList
                schoolId={schoolId || ''}
                schoolName={school?.name || ''}
              />
            )}
            {activeTab === 'teachers' && (
              <TeacherList
                schoolId={schoolId || ''}
                schoolName={school?.name || ''}
              />
            )}
         </div>
               </div>


       
     </div>
   );
 };

export default SchoolDetailPage; 